import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";

export const CustomizationSettings = () => {
  const [theme, setTheme] = useState("system");
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [pushNotifications, setPushNotifications] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      const { data } = await supabase
        .from("user_preferences" as any)
        .select("*")
        .eq("user_id", user.id)
        .single();
      if (data) {
        setTheme((data as any).theme || "system");
        setEmailNotifications((data as any).email_notifications ?? true);
        setPushNotifications((data as any).push_notifications ?? false);
      }
    } catch {
      // No preferences yet — use defaults
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async (updates: Record<string, any>) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: existing } = await supabase
        .from("user_preferences" as any)
        .select("id")
        .eq("user_id", user.id)
        .single();

      if (existing) {
        await supabase
          .from("user_preferences" as any)
          .update(updates)
          .eq("user_id", user.id);
      } else {
        await supabase
          .from("user_preferences" as any)
          .insert({ user_id: user.id, ...updates });
      }
    } catch (error) {
      console.error("Failed to save preferences:", error);
    }
  };

  const handleThemeChange = (value: string) => {
    setTheme(value);
    savePreferences({ theme: value });
    toast.success(`Theme updated to ${value}`);
  };

  const handleEmailToggle = (checked: boolean) => {
    setEmailNotifications(checked);
    savePreferences({ email_notifications: checked });
    toast.success(`Email notifications ${checked ? "enabled" : "disabled"}`);
  };

  const handlePushToggle = (checked: boolean) => {
    setPushNotifications(checked);
    savePreferences({ push_notifications: checked });
    toast.success(`Push notifications ${checked ? "enabled" : "disabled"}`);
  };

  if (loading) return null;

  return (
    <Card className="p-6">
      <h2 className="text-lg font-semibold mb-4">Customization Settings</h2>
      <div className="space-y-6">
        <div className="space-y-2">
          <Label>Dashboard Theme</Label>
          <Select onValueChange={handleThemeChange} value={theme}>
            <SelectTrigger>
              <SelectValue placeholder="Select theme" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="light">Light</SelectItem>
              <SelectItem value="dark">Dark</SelectItem>
              <SelectItem value="system">System</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-4">
          <Label>Notifications</Label>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="email-notifications">Email Notifications</Label>
                <p className="text-sm text-muted-foreground">
                  Receive updates via email
                </p>
              </div>
              <Switch
                id="email-notifications"
                checked={emailNotifications}
                onCheckedChange={handleEmailToggle}
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="push-notifications">Push Notifications</Label>
                <p className="text-sm text-muted-foreground">
                  Receive browser notifications
                </p>
              </div>
              <Switch
                id="push-notifications"
                checked={pushNotifications}
                onCheckedChange={handlePushToggle}
              />
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};
